import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PLANNED_PRICES, SCENE_LABEL, type ProjectView, type Scene, type ShotView } from "@joogoos/contracts";
import { showreelSpoken } from "@joogoos/contracts/showreel";
import { api } from "../../api.js";

/**
 * A drawn scene's words, changed in the editor (ADR 0050). Every text a scene draws, in the words a person would use
 * for it, and nothing else: the kind, the backdrop and the cast stay what the script chose. Saving draws the video
 * again, which is one render's charge; the server holds every field to the scene's own limits. In a video of pieces
 * (Showreel, Editorial, Poster, Handmade) the voice says a piece's lines (2026-09-28), so new words on a piece that
 * speaks are said again, for a voice more.
 */
const LABEL: Record<string, string> = {
  eyebrow: "Small line above", headline: "Headline", accent: "Words in colour", value: "Number", prefix: "Before the number",
  suffix: "After the number", caption: "Caption", title: "Document", lines: "Lines", label: "Line", amount: "Amount",
  flag: "What is wrong", totalLabel: "Total label", total: "Total", steps: "Steps", detail: "Detail", stamp: "Stamp",
  button: "Button", address: "Website or phone", sub: "Under the caption", flags: "Wrong lines",
  before: "First side, crossed out", after: "Second side, ticked", points: "Points", point: "Point", line: "Line",
  figure: "Figure",
};
/** What one item of a list of words is called: a list of objects (a document's lines) names its own fields. */
const ITEM: Record<string, string> = { flags: "flag", points: "point", lines: "line" };
/**
 * Choices from a list, not words: they are not the owner's to type. A showreel's piece is one (ADR 0058), and a photo
 * piece's photo and its regions another (2026-09-29): kept as they are and sent back unchanged, since the server keeps
 * them.
 */
const NOT_WORDS = new Set(["kind", "backdrop", "cast", "pose", "piece", "photo"]);
/**
 * Fields shown only when the scene has them. A showreel's figure is an object or nothing, so an empty one is not a box:
 * typing into it would send a string where the schema wants a number and its signs, and the save would be refused.
 */
const ONLY_WHEN_SET = new Set(["figure"]);

/**
 * What the Studio calls a scene's kind, and something plain for a kind this build does not know: a tab left open over a
 * release that adds one, or a release rolled back under a scene the newer one wrote, must still draw the shot.
 */
export const sceneLabel = (kind: string) => (SCENE_LABEL as Record<string, string | undefined>)[kind] ?? "Scene";

type Value = string | number | null | Value[] | { [k: string]: Value };

function Field({ name, value, onChange }: { name: string; value: Value; onChange: (v: Value) => void }) {
  const label = LABEL[name] ?? name;
  if (typeof value === "number") {
    return (
      <label className="flex flex-col gap-0.5">
        <span className="text-[11px] text-muted">{label}</span>
        <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} className="border border-line-2 rounded-lg px-2 py-1 text-xs bg-panel" />
      </label>
    );
  }
  // A group of words, one side of a before and after: its own words, under its name.
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    return (
      <fieldset className="flex flex-col gap-1.5 pl-2 border-l-2 border-line">
        <legend className="text-[11px] text-muted mb-0.5">{label}</legend>
        {Object.entries(value).map(([k, v]) => <Field key={k} name={k} value={v} onChange={(nv) => onChange({ ...value, [k]: nv })} />)}
      </fieldset>
    );
  }
  if (Array.isArray(value)) {
    return (
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-[11px] text-muted mb-0.5">{label}</legend>
        {value.map((item, i) => (
          <div key={i} className="flex flex-col gap-1 pl-2 border-l-2 border-line">
            {typeof item === "object" && item !== null && !Array.isArray(item)
              ? Object.entries(item).map(([k, v]) => <Field key={k} name={k} value={v} onChange={(nv) => onChange(value.map((x, j) => (j === i ? { ...(x as object), [k]: nv } : x)))} />)
              : <Field name={ITEM[name] ?? name} value={item} onChange={(nv) => onChange(value.map((x, j) => (j === i ? nv : x)))} />}
          </div>
        ))}
      </fieldset>
    );
  }
  // A field that may be empty is null in the scene, and an empty box here.
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[11px] text-muted">{label}</span>
      <input value={(value as string | null) ?? ""} onChange={(e) => onChange(value === null && e.target.value === "" ? null : e.target.value)} className="border border-line-2 rounded-lg px-2 py-1 text-xs bg-panel" />
    </label>
  );
}

/** Empty boxes back to the nulls the scene's schema expects, where the field may be empty. */
const tidy = (before: Value, after: Value): Value => {
  if (Array.isArray(after)) return after.map((v, i) => tidy(Array.isArray(before) ? before[i] ?? null : null, v));
  if (after && typeof after === "object") return Object.fromEntries(Object.entries(after).map(([k, v]) => [k, tidy(before && typeof before === "object" && !Array.isArray(before) ? before[k] ?? null : null, v)]));
  return after === "" && before === null ? null : after;
};

export function SceneWords({ project, shot, onDone }: { project: ProjectView; shot: ShotView & { scene: Scene }; onDone: () => void }) {
  const qc = useQueryClient();
  const [scene, setScene] = useState<Record<string, Value>>(shot.scene as unknown as Record<string, Value>);
  const save = useMutation({
    mutationFn: () => api.editScene(project.id, shot.id, tidy(shot.scene as unknown as Value, scene) as unknown as Scene),
    onSuccess: (p) => { qc.setQueryData(["project", project.id], p); onDone(); },
  });
  const busy = project.status === "scripted" || project.status === "generating";
  // Said again exactly when the server says it again (the scene's PUT in apps/api routes/projects.ts), from what the
  // server decides by: a narrated piece of a script written for a video of pieces says its lines, and a voice is made
  // when what it says changes, or when a shot that speaks has no voice, which is what a voice that gave up leaves. The
  // price counts it as the plan does, a voice and its timings. Reading the draft and the old voice priced three cases
  // wrong (review, 2026-09-28).
  const edited = tidy(shot.scene as unknown as Value, scene) as unknown as Scene;
  const piece = edited.kind === "showreel" && shot.isNarration && !!project.script?.showreel ? edited : null;
  const says = piece ? showreelSpoken(piece).text || null : shot.dialogue;
  const revoice = says !== shot.dialogue || (!!says && shot.isNarration && !(shot.assets.voice && shot.assets.captions));
  const usd = (PLANNED_PRICES.render ?? 0) + (revoice ? (PLANNED_PRICES.tts ?? 0) + (PLANNED_PRICES.align ?? 0) : 0);
  return (
    <form
      aria-label="The scene's words"
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
      className="bg-panel border border-line rounded-[10px] p-3 flex flex-col gap-2 overflow-y-auto min-h-0"
    >
      {Object.entries(scene).filter(([k, v]) => !NOT_WORDS.has(k) && !(ONLY_WHEN_SET.has(k) && v === null)).map(([k, v]) => (
        <Field key={k} name={k} value={v} onChange={(nv) => setScene((s) => ({ ...s, [k]: nv }))} />
      ))}
      {save.error ? <p role="alert" className="text-[11px] text-bad">{(save.error as Error).message}</p> : null}
      <div className="flex items-center gap-2 pt-1">
        <button type="submit" disabled={save.isPending || busy} className="px-3 py-1.5 rounded-lg bg-coral text-white text-xs font-semibold disabled:opacity-50">
          Save and redraw{revoice ? " with its voice" : ""} · about ${usd.toFixed(2)}
        </button>
        <button type="button" onClick={onDone} className="px-3 py-1.5 rounded-lg bg-page text-ink-2 text-xs font-medium hover:bg-line">Cancel</button>
      </div>
      {revoice ? <p className="text-[11px] text-muted">Its voice says these words, so it is made again.</p> : null}
      {busy ? <p className="text-[11px] text-muted">The video is still being made; its words can change once it is done.</p> : null}
    </form>
  );
}
